// 受渡日 (当日 / 翌日 / 任意日) を選べるようにしたことで必要になった、
// 「その受渡日から見た GC 状態」 の導出。
//
// design `design/bidding/intraday.html` の `_intradayGcSlot` (竹モック
// `CalcGcForDeliveryDate` 準拠) を基にしつつ、 GC 判定はコマ番号の近似ではなく
// GC 時刻そのもので行う:
//   過去日 → 全コマ締切済 / 当日・未来日 → その受渡日の GC 時刻と現在時刻の比較。
// (モックの「未来日 → 全コマ受付中」 は、 コマ 1 / 2 の GC が前日 23:00 / 23:30 と
//  前日にまたがるため深夜帯で崩れる。)
import { KOMA_COUNT } from '~/constants/koma'
import { isSameDay, startOfTodayJst } from '~/models/dateTime'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'

/** 全 48 コマが GC 通過済みであることを表す境界値 (= コマ 48 も締切済)。 */
const ALL_CLOSED_BOUNDARY = 49

/**
 * 受渡日 (= カレンダー日 `Date`) の時刻部分を落として暦日だけにする。
 * `now` (= 絶対時刻) 側は [[startOfTodayJst]] で **JST の暦日**に写してから比べる
 * (= 端末ローカルの暦日で比べると非 JST 端末で受渡日が 1 日ズレる、 #4765)。
 */
const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())

/** 受渡日が JST の今日より過去か。 */
const isPastDate = (now: Date, deliveryDate: Date): boolean =>
  startOfDay(deliveryDate).getTime() < startOfTodayJst(now).getTime()

/** 受渡日が JST の今日か。 */
const isTodayInJst = (now: Date, deliveryDate: Date): boolean =>
  isSameDay(startOfTodayJst(now), deliveryDate)

/**
 * その受渡日で GC 時刻 (= [[calcGcAtForKoma]] = コマ開始 1 時間前) がまだ到来して
 * いない最小のコマ。 全コマ GC 済なら [[ALL_CLOSED_BOUNDARY]]。
 *
 * 判定は「コマ開始 − 60 分 <= 現在時刻 なら GC 済」 を GC 時刻そのもので行う
 * (= コマ番号の加減算で近似しない)。 旧実装の「現在進行中コマ + 2」 は起点が
 * 進行中コマなので境界が 1 コマ (= 30 分) 手前にズレ、 実質「GC = 30 分前」 の
 * 挙動になっていた。
 *
 * 業務例 (docs/specs/bidding/intraday-bidding.md「GC = 受渡コマ開始の1時間前」、
 * mock-app/docs/domain-knowledge.md §3): 受渡日 = 当日で 14:00 時点ではコマ 31
 * (15:00〜15:30、 GC 時刻 14:00) まで GC 済なので、 境界 = コマ 32。
 *
 * 受渡日は当日に限らない: GC はコマ開始の 1 時間前なので受渡日 D のコマ 1 の GC は
 * D-1 の 23:00、 コマ 2 は D-1 の 23:30 と**前日にまたがる**。 未来日を「全コマ
 * 受付中」 で固定すると、 当日 23 時台に翌日を選んだときに GC 済のコマ 1 / 2 が
 * 受付中として出てしまうため、 当日・未来日は同じ導出に載せる。
 */
const gcBoundarySlotFor = (now: Date, deliveryDate: Date): number => {
  for (let koma = 1; koma <= KOMA_COUNT; koma++) {
    if (calcGcAtForKoma(deliveryDate, koma).getTime() > now.getTime()) {
      return koma
    }
  }
  return ALL_CLOSED_BOUNDARY
}

/**
 * GC 境界コマ (= このコマより前のコマは GC 通過済み、 1〜49)。
 * 当日・未来日は GC 時刻ベース ([[gcBoundarySlotFor]])、 過去日は全コマ締切済。
 */
export const gcBoundarySlot = (now: Date, deliveryDate: Date): number =>
  isPastDate(now, deliveryDate)
    ? ALL_CLOSED_BOUNDARY
    : gcBoundarySlotFor(now, deliveryDate)

/**
 * まだ入札できる最も早いコマ (= GC 境界コマ、 1〜48)。
 * 過去日は全 48 コマが締切済なので undefined (= 入札導線を出さない)。
 *
 * `gcBoundarySlot` は「これ未満は締切済」 を表す境界なので過去日に 49 を返すが、
 * 49 は実在しないコマなので、 コマ番号を要求する導線 (= 個別作成メニュー) は
 * こちらを使う。
 */
export const firstOpenSlot = (
  now: Date,
  deliveryDate: Date,
): number | undefined => {
  const boundary = gcBoundarySlot(now, deliveryDate)
  return boundary > KOMA_COUNT ? undefined : boundary
}

/**
 * chart / ribbon に GC 基準線を引くコマ (= 当日で、 まだ GC を迎えていないコマが
 * 残っている場合のみ)。 別日は「今まさに GC を迎えるコマ」 が存在せず、 当日でも
 * 全コマ GC 済 (= 深夜帯) なら基準線を引くコマが無いので undefined。
 */
export const gcLineSlot = (
  now: Date,
  deliveryDate: Date,
): number | undefined =>
  isTodayInJst(now, deliveryDate) ? firstOpenSlot(now, deliveryDate) : undefined

/**
 * コマの GC 状態 (canonical): `calcGcAtForKoma` の実時刻と `now` を直接比較する
 * (= `gcBoundarySlot` のような「コマ番号 < 境界コマ番号」比較と違い、 現在コマの
 * 途中で GC を迎える境界コマ自身や、 未来受渡日のコマ 1/2 も正しく締切済と判定できる)。
 * - 'closed': GC 通過済 (= 選択・作成不可)
 * - 'imminent': 当日のみ、 今まさに GC を迎えるコマ (= 直前のコマは GC 通過済、
 *   このコマはまだ通過前)。 別日は「今」 の基準が無いので常に 'closed' | 'open'。
 * - 'open': まだ GC 前
 */
export const gcStatusOfKoma = (
  now: Date,
  deliveryDate: Date,
  koma: number,
): 'closed' | 'imminent' | 'open' => {
  if (calcGcAtForKoma(deliveryDate, koma).getTime() <= now.getTime()) {
    return 'closed'
  }
  if (
    isTodayInJst(now, deliveryDate) &&
    koma > 1 &&
    calcGcAtForKoma(deliveryDate, koma - 1).getTime() <= now.getTime()
  ) {
    return 'imminent'
  }
  return 'open'
}

/**
 * 次にカウントダウン対象とする GC 時刻と対象コマ。
 *
 * 当日・未来日とも「まだ入札できる最初のコマ」 ([[firstOpenSlot]]) の GC を返す。
 * 未来日を「常にコマ 1 の GC」 で固定すると、 コマ 1 の GC (= 前日 23:00) を過ぎた
 * 時点でカウントダウンだけ過去時刻 (= 「GC 済」 表示) になる一方、 リボン上ではコマが
 * 受付中という表示矛盾が起きるため、 境界導出と同じ関数に載せる。
 *
 * 過去日と全コマ GC 済 (= 当日深夜帯) はカウントダウン対象が無いので undefined
 * (= GcCountdown が「GC 済」 表示)。
 */
export const nextGcForDeliveryDate = (
  now: Date,
  deliveryDate: Date,
): { gcAt: Date | undefined; targetKoma: number | undefined } => {
  const koma = firstOpenSlot(now, deliveryDate)
  if (koma === undefined) {
    return { gcAt: undefined, targetKoma: undefined }
  }
  return { gcAt: calcGcAtForKoma(deliveryDate, koma), targetKoma: koma }
}
